import { useState, useEffect, useRef } from 'react'
import { Bot, RotateCcw, Save, Copy, Sparkles, Check, Star, Loader2, Languages } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import type { Review, DraftTone } from '@/lib/api/reviews'
import { generateDraft, getDraft, updateDraft } from '@/lib/api/reviews'
import { formatDistanceToNow, formatNumber } from '@/lib/format'
import { useReviewLanguage } from '@/hooks/use-review-language'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

interface AIResponsePanelProps {
  selectedReview: Review | null
  onGenerateTriggered?: (reviewId: number, isLoading: boolean) => void
  onDraftGenerated?: (reviewId: number) => void
  onDraftSaved?: (reviewId: number) => void
}

const toneOptions: { value: DraftTone; labelKey: string }[] = [
  { value: 'professional', labelKey: 'reviews.tone.professional' },
  { value: 'friendly', labelKey: 'reviews.tone.friendly' },
  { value: 'apologetic', labelKey: 'reviews.tone.apologetic' },
  { value: 'brief', labelKey: 'reviews.tone.brief' },
]

// Languages the picker offers as explicit overrides. The wider set the LLM
// can actually produce is much broader — these are just the common ones the
// operator might want to pin to. Default behavior is "auto" → backend uses
// the review's detected language.
const RESPONSE_LANGUAGES: { code: string; labelKey: string }[] = [
  { code: 'en', labelKey: 'reviews.language.en' },
  { code: 'es', labelKey: 'reviews.language.es' },
  { code: 'pt', labelKey: 'reviews.language.pt' },
  { code: 'fr', labelKey: 'reviews.language.fr' },
  { code: 'de', labelKey: 'reviews.language.de' },
  { code: 'it', labelKey: 'reviews.language.it' },
  { code: 'nl', labelKey: 'reviews.language.nl' },
  { code: 'ja', labelKey: 'reviews.language.ja' },
  { code: 'zh', labelKey: 'reviews.language.zh' },
]

function languageLabel(t: TFunction, code: string | null | undefined): string {
  if (!code) return t('reviews.languageUnknown')
  const match = RESPONSE_LANGUAGES.find((l) => l.code === code.toLowerCase())
  return match ? t(match.labelKey) : code.toUpperCase()
}

interface ReviewBodyProps {
  review: Review
}

/** Renders the review text with a toggle to flip between Google's translation
 * and the customer's original-language text, when both exist. */
function ReviewBody({ review }: ReviewBodyProps) {
  const { t } = useTranslation()
  const { text: displayed, canToggle, showingOriginal, toggle } = useReviewLanguage(review)

  if (!displayed) return null

  return (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground leading-relaxed">{displayed}</p>
      {canToggle && (
        <button
          type="button"
          onClick={toggle}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
        >
          <Languages className="h-3 w-3" />
          {showingOriginal ? t('reviews.showTranslation') : t('reviews.showOriginal')}
        </button>
      )}
    </div>
  )
}

interface ExpandableTextProps {
  text: string
  className?: string
}

function ExpandableText({ text, className = '' }: ExpandableTextProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight)
    }
  }, [text])

  return (
    <div>
      <p ref={textRef} className={`${className} ${!isExpanded ? 'line-clamp-3' : ''}`}>
        {text}
      </p>
      {isTruncated && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-primary hover:underline mt-1"
        >
          {isExpanded ? t('reviews.showLess') : t('reviews.showMore')}
        </button>
      )}
    </div>
  )
}

export function AIResponsePanel({
  selectedReview,
  onGenerateTriggered,
  onDraftGenerated,
  onDraftSaved,
}: AIResponsePanelProps) {
  const { t } = useTranslation()

  const formatRelativeTime = (dateString: string | null | undefined): string => {
    if (!dateString) return t('reviews.unknownDate')
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return dateString
    }
  }

  const [response, setResponse] = useState('')
  const [tone, setTone] = useState<DraftTone>('professional')
  // 'auto' = let the backend resolve to the review's detected language.
  // Anything else = explicit 2-letter language code passed straight through.
  const [language, setLanguage] = useState<string>('auto')
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  // Reset the language picker to "auto" whenever the user opens a different
  // review — each review has its own customer language; carrying a manual
  // override between reviews would be confusing.
  useEffect(() => {
    setLanguage('auto')
  }, [selectedReview?.id])

  // Load existing draft when review changes
  useEffect(() => {
    if (!selectedReview || selectedReview.owner_response) {
      setResponse('')
      return
    }

    async function loadDraft() {
      if (!selectedReview) return
      try {
        const draft = await getDraft(selectedReview.id)
        if (draft) {
          setResponse(draft.content)
        } else {
          setResponse('')
        }
      } catch {
        setResponse('')
      }
    }

    loadDraft()
  }, [selectedReview?.id, selectedReview?.owner_response])

  const handleGenerateResponse = async () => {
    if (!selectedReview) return

    setIsGenerating(true)
    onGenerateTriggered?.(selectedReview.id, true)
    try {
      const draft = await generateDraft(selectedReview.id, tone, language)
      setResponse(draft.content)
      onDraftGenerated?.(selectedReview.id)
    } catch (error) {
      toast.error(t('reviews.toast.generateFailed'))
      console.error('Failed to generate response:', error)
    } finally {
      setIsGenerating(false)
      onGenerateTriggered?.(selectedReview.id, false)
    }
  }

  const handleSave = async () => {
    if (!selectedReview || !response.trim()) return

    setIsSaving(true)
    try {
      await updateDraft(selectedReview.id, response)
      toast.success(t('reviews.toast.draftSaved'))
      onDraftSaved?.(selectedReview.id)
    } catch (error) {
      toast.error(t('reviews.toast.saveFailed'))
      console.error('Failed to save draft:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCopyResponse = () => {
    navigator.clipboard.writeText(response)
    setIsCopied(true)
    toast.success(t('reviews.toast.copied'))
    setTimeout(() => setIsCopied(false), 2000)
  }

  if (!selectedReview) {
    return (
      <Card className="h-full shadow-none border-border/50">
        <CardContent className="p-12 text-center flex flex-col items-center justify-center min-h-[400px]">
          <div className="space-y-4 max-w-sm mx-auto">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto">
              <Bot className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="space-y-1.5">
              <h3 className="font-medium">{t('reviews.noReviewSelected')}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('reviews.noReviewSelectedDescription')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // If review already has a response, show it
  if (selectedReview.owner_response) {
    return (
      <div className="space-y-6">
        {/* Selected Review Display */}
        <div className="space-y-3">
          <h3 className="font-medium text-sm text-muted-foreground">
            {t('reviews.selectedReview')}
          </h3>
          <Card className="shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarFallback className="bg-gradient-to-br from-accent/30 to-primary/30 font-medium">
                    {selectedReview.author_name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{selectedReview.author_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(selectedReview.published_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= selectedReview.rating
                            ? 'fill-primary text-primary'
                            : 'text-muted-foreground'
                        }`}
                      />
                    ))}
                  </div>
                  <ReviewBody review={selectedReview} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Existing Response */}
        <Card className="shadow-sm border-accent/30 bg-accent/5">
          <CardHeader className="pb-3 pt-5 px-5">
            <CardTitle className="flex items-center gap-2 text-base">
              <Check className="h-5 w-5 text-accent" />
              {t('reviews.responseAlreadyPosted')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-3">
            <ExpandableText
              text={selectedReview.owner_response}
              className="text-sm leading-relaxed"
            />
            {selectedReview.owner_response_at && (
              <p className="text-xs text-muted-foreground">
                {t('reviews.postedRelative', {
                  time: formatRelativeTime(selectedReview.owner_response_at),
                })}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Selected Review Display */}
      <div className="space-y-3">
        <h3 className="font-medium text-sm text-muted-foreground">{t('reviews.selectedReview')}</h3>
        <Card className="shadow-sm border-primary/30">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarFallback className="bg-gradient-to-br from-accent/30 to-primary/30 font-medium">
                  {selectedReview.author_name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{selectedReview.author_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatRelativeTime(selectedReview.published_at)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`h-4 w-4 ${
                        star <= selectedReview.rating
                          ? 'fill-primary text-primary'
                          : 'text-muted-foreground'
                      }`}
                    />
                  ))}
                </div>
                <ReviewBody review={selectedReview} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Response Panel */}
      <Card className="shadow-sm border-border/50">
        <CardHeader className="pb-4 pt-5 px-5 space-y-1">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              <span>{t('reviews.aiResponseGenerator')}</span>
            </div>
            <Badge
              variant="outline"
              className="bg-primary/10 text-primary border-primary/30 text-xs font-normal"
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Savorias AI
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-5">
          {/* Tone Selector */}
          <div className="space-y-3">
            <label className="text-sm font-medium">{t('reviews.responseTone')}</label>
            <div className="flex flex-wrap gap-2">
              {toneOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTone(option.value)}
                  className={`
                    px-3 py-1.5 rounded-md border text-sm font-medium transition-colors
                    ${
                      tone === option.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                  `}
                >
                  {t(option.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* Language Selector */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label htmlFor="reply-language" className="text-sm font-medium">
                {t('reviews.replyIn')}
              </label>
              {selectedReview?.analysis?.language && (
                <span className="text-xs text-muted-foreground">
                  {t('reviews.reviewDetectedAs')}{' '}
                  <span className="font-medium text-foreground">
                    {languageLabel(t, selectedReview.analysis.language)}
                  </span>
                </span>
              )}
            </div>
            <select
              id="reply-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            >
              <option value="auto">
                {t('reviews.languageAuto')}
                {selectedReview?.analysis?.language
                  ? ` (${languageLabel(t, selectedReview.analysis.language)})`
                  : ''}
              </option>
              {RESPONSE_LANGUAGES.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Response Text Area */}
          <div className="space-y-3">
            <label className="text-sm font-medium">{t('reviews.generatedResponse')}</label>
            <div className="bg-accent/5 p-4 rounded-lg border border-accent/20">
              {isGenerating ? (
                <div className="space-y-2 py-1 min-h-[140px]">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-4/6" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/6" />
                </div>
              ) : (
                <Textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  className="min-h-[140px] resize-none bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  placeholder={t('reviews.responsePlaceholder')}
                />
              )}
              <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                <span>
                  {t('reviews.charactersCount', {
                    count: response.length,
                    value: formatNumber(response.length),
                  })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleCopyResponse}
                  disabled={isGenerating || !response}
                >
                  {isCopied ? (
                    <>
                      <Check className="h-3 w-3 mr-1 text-accent" />
                      {t('reviews.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3 mr-1" />
                      {t('reviews.copy')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleGenerateResponse}
              className="flex-1"
              disabled={isGenerating || isSaving}
            >
              {isGenerating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              {isGenerating
                ? t('reviews.generating')
                : response
                  ? t('reviews.regenerate')
                  : t('reviews.generate')}
            </Button>
            <Button
              variant="outline"
              onClick={handleSave}
              className="flex-1"
              disabled={isGenerating || isSaving || !response.trim()}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isSaving ? t('reviews.saving') : t('reviews.saveDraft')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
